// @ts-strict-ignore
import { StringUtils } from '@shared/util/string-utils';
import { UserProfileVO, UserVO } from '@user/model/vo/user.vo';
import { UserProfileUtils } from '../service/user-profile-utils';
import { ArticleNumberMappingListVO, ArticleNumberMappingVO } from '@article/model/vo/article-number-mapping.vo';
import { ArticleNumberMappingUtils } from '@article/util/article-number-mapping-utils';
import { LocaleResourceManager } from '@shared/manager/locale-resource.manager';
import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { UserPermissionName } from '../constants/user-permissions';

@Injectable({ providedIn: 'root' })
export class UserModel {

  public isConstruction: boolean = false;

  private _oldUserProfileRegistered: boolean = false;
  private _articleNumberMappingLists: ArticleNumberMappingListVO[];
  private _activeArticleNumberMappingList: ArticleNumberMappingListVO;

  private _userProfile$: BehaviorSubject<UserProfileVO> = new BehaviorSubject(null);

  constructor(
    private localeResourceManager: LocaleResourceManager
  ) {
  }

  public get userProfile(): UserProfileVO {
    return this._userProfile$.getValue();
  }

  public get userProfile$(): Observable<UserProfileVO> {
    return this._userProfile$.asObservable();
  }

  /*
  Use UserProfileService to set the user profile!
   */
  public set userProfile(userProfile: UserProfileVO) {
    const oldUserProfile: UserProfileVO = this.userProfile;
    let resetOldUserProfile: boolean = false;
    if (oldUserProfile) {
      this._oldUserProfileRegistered = oldUserProfile.isRegisteredUserAccount();
      if (!this._oldUserProfileRegistered) {
        UserProfileUtils.mergeArticleCollectionsInUserProfiles(this.userProfile, userProfile);
        resetOldUserProfile = true;
      }
    }
    UserProfileUtils.secureExistenceOfFavoritesArticleCollection(userProfile);
    this._userProfile$.next(userProfile);
    if (resetOldUserProfile) {
      UserProfileUtils.resetUserProfile(oldUserProfile);
    }
  }

  public set articleNumberMappingLists(articleNumberMappingLists: ArticleNumberMappingListVO[]) {
    this._articleNumberMappingLists = articleNumberMappingLists;
    if (this._articleNumberMappingLists) {
      this._activeArticleNumberMappingList = articleNumberMappingLists.find((list: ArticleNumberMappingListVO) => list.isActive);
    } else {
      this._activeArticleNumberMappingList = null;
    }
  }

  public get articleNumberMappingLists(): ArticleNumberMappingListVO[] {
    return this._articleNumberMappingLists;
  }

  public get articleNumberMappings(): ArticleNumberMappingVO[] {
    if (!this._activeArticleNumberMappingList) {
      return null;
    }
    return this._activeArticleNumberMappingList.entries;
  }

  public findArticleNumberByOwnArticleNumber(ownArticleNumber: string): string | undefined {
    if(StringUtils.isEmpty(ownArticleNumber)) return null
    for (const list of this._articleNumberMappingLists) {
      for (const numberMapping of list.entries) {
        if (StringUtils.trim(numberMapping.customArticleNumber) === StringUtils.trim(ownArticleNumber)) {
          return numberMapping.articleNumber;
        }
      }
    }
    return undefined;
  }

  public get user(): UserVO {
    if (!this.userProfile) {
      return null;
    }
    return this.userProfile.user;
  }

  public get isUserLoggedIn(): boolean {
    const user: UserVO = this.user;
    if (!user) {
      return false;
    }
    return user.isRegisteredUserAccount();
  }

  public isGuestUser(userProfile: UserProfileVO = this.userProfile): boolean {
    return userProfile && userProfile.user.id === UserVO.DEFAULT_GUEST_ACCOUNT_ID;
  }

  public isRegisteredUserAccount(userProfile: UserProfileVO = this.userProfile): boolean {
    return userProfile && userProfile.isRegisteredUserAccount();
  }

  public isCustomerUser(userProfile: UserProfileVO = this.userProfile): boolean {
    return userProfile && userProfile.user.isCustomer();
  }

  public getCustomArticleNumber(articleNo: string, defaultValue: string = ``): string {
    let result = defaultValue;
    const mapping: ArticleNumberMappingVO = ArticleNumberMappingUtils.findMappingByArticleNumber(articleNo, this.articleNumberMappings);
    if (mapping) {
      result = mapping.customArticleNumber;
    }
    return result;
  }

  public getActiveArticleNumberMappingListName(minRequiredListsCount: number = 2): string {
    if (!this._activeArticleNumberMappingList) {
      return null;
    }
    if (!this._articleNumberMappingLists || this._articleNumberMappingLists.length < minRequiredListsCount) {
      return null;
    }
    return this._activeArticleNumberMappingList.isDefault
      ? this.localeResourceManager.getValueByKey(this._activeArticleNumberMappingList.name)
      : this._activeArticleNumberMappingList.name;
  }

  public checkPermissions(permissionNames: UserPermissionName[], grantUnregisteredUserAccountsAccess: boolean = true): boolean {
    return (
      this.user &&
      (this.user.hasPermissions(permissionNames) ||
        (grantUnregisteredUserAccountsAccess && this.isGuestUser(this.userProfile)))
    );
  }
}
